import { useMemo } from "react";
import classNames from "classnames";

import { AscBadgeColor } from "@ascendnow/shared";

import AscBadge from "../../AscBadge/AscBadge";
import AscButton from "../../AscButton/AscButton";
import { AwardIcon } from "../../AscIcons";
import { StatSparkIllustration } from "../../AscIllustrations";
import AscLink from "../../AscLink/AscLink";
import AscProgressBar from "../../AscProgressBar/AscProgressBar";
import AscGenericCard from "../AscGenericCard/AscGenericCard";
import styles from "./AscSkillProgressCard.module.scss";

export interface AscSkillProgressCardProps {
  className?: string;
  skillTitle: string;
  numberOfModules: number;
  pillarGrade?: number;
  completion?: number;
  totalModules?: number;
  completedModules?: number;
  totalChallenges?: number;
  completedChallenges?: number;
  totalWebinars?: number;
  completedWebinars?: number;
  perModuleAnalyticsLink?: string;
  perModuleAnalyticsClick?: () => void;
  badgeColor?: AscBadgeColor;
  noStatsTitle?: string;
  noStatsDescription?: string;
  noRedirectBtn?: boolean;
  hideModules?: boolean;
  hideChallenges?: boolean;
  hideWebinars?: boolean;
  theme?: "light" | "dark";
  tag?: string;
  loading?: boolean;
}

export default function AscSkillProgressCard({
  className,
  skillTitle,
  numberOfModules = 0,
  pillarGrade = 0,
  completion = 0,
  totalModules = 0,
  completedModules = 0,
  totalChallenges = 0,
  completedChallenges = 0,
  totalWebinars = 0,
  completedWebinars = 0,
  hideModules = false,
  hideChallenges = false,
  hideWebinars = false,
  perModuleAnalyticsLink,
  perModuleAnalyticsClick,
  badgeColor = "TEAL",
  noStatsTitle = "No stats",
  noStatsDescription = "You don't have any statistics at the moment. Start your first lesson on the platform!",
  noRedirectBtn = false,
  theme = "light",
  tag,
  loading = false
}: AscSkillProgressCardProps) {
  const isEmpty = useMemo(() => {
    return (
      !pillarGrade &&
      !completion &&
      !completedChallenges &&
      !completedModules &&
      !completedWebinars
    );
  }, [
    completion,
    pillarGrade,
    completedChallenges,
    completedModules,
    completedWebinars,
  ]);

  return (
    <AscGenericCard
      title={""}
      bodyClassName={styles.body}
      data-testid="asc-skill-progress-card"
      tags={tag ? [tag] : []}
      tagsContainerClassName={styles.tag}
      className={classNames(
        styles.ground,
        {
          [styles.loading]: loading,
          [styles.dark]: theme === "dark",
          [styles.hasTag]: Boolean(tag),
        },
        className
      )}
      customImage={<AscBadge color={badgeColor} className={styles.badge} />}
      footer={
        isEmpty || noRedirectBtn ? null : (
          <AscLink
            innerWrapperTag={null}
            className={styles.link}
            href={perModuleAnalyticsLink}
            onClick={() => perModuleAnalyticsClick?.()}
          >
            <AscButton fullWidth narrowPadding>
              Per Module Analytics
            </AscButton>
          </AscLink>
        )
      }
      footerClassName={styles.footer}
      contentClassName={styles.content}
    >
      <div
        className={classNames(styles.contentHead, {
          [styles.reverseOrder]: hideChallenges && hideModules && hideWebinars,
        })}
      >
        <h5 className={classNames(styles.title)}>{skillTitle}</h5>
        <div className={styles.modules}>
          <AwardIcon className={styles.moduleIcon} />
          <span className={styles.moduleText}>{numberOfModules} Modules</span>
        </div>
      </div>

      {isEmpty ? (
        <div className={styles.emptyContainer}>
          <StatSparkIllustration className={styles.emptyIcon} />
          {noStatsTitle && (
            <div className={styles.emptyTitle}>{noStatsTitle}</div>
          )}
          {noStatsDescription && (
            <div className={styles.emptyDescription}>{noStatsDescription}</div>
          )}
        </div>
      ) : (
        <>
          <div className={styles.percentageContainer}>
            <div className={styles.percentageLabelsContainer}>
              <div className={styles.percentageName}>Pillar Grade</div>
              <div className={styles.percentageValue}>
                {pillarGrade?.toFixed(2)}%
              </div>
            </div>
            <AscProgressBar
              fullWidth
              className={classNames(styles.percentageBar, styles[badgeColor])}
              value={pillarGrade}
              color={badgeColor}
            />
          </div>
          <div className={styles.percentageContainer}>
            <div className={styles.percentageLabelsContainer}>
              <div className={styles.percentageName}>Completion</div>
              <div className={styles.percentageValue}>
                {completion?.toFixed(2)}%
              </div>
            </div>
            <AscProgressBar
              fullWidth
              className={classNames(styles.percentageBar, styles[badgeColor])}
              value={completion}
              color={badgeColor}
            />
          </div>
          {!hideModules && (
            <div className={styles.percentageContainer}>
              <div className={styles.percentageLabelsContainer}>
                <div className={styles.percentageName}>Modules</div>
                <div className={styles.percentageValue}>
                  {completedModules}/{totalModules}
                </div>
              </div>
            </div>
          )}
          {!hideChallenges && (
            <div className={styles.percentageContainer}>
              <div className={styles.percentageLabelsContainer}>
                <div className={styles.percentageName}>Challenges</div>
                <div className={styles.percentageValue}>
                  {completedChallenges}/{totalChallenges}
                </div>
              </div>
            </div>
          )}
          {!hideWebinars && (
            <div className={styles.percentageContainer}>
              <div className={styles.percentageLabelsContainer}>
                <div className={styles.percentageName}>Events</div>
                <div className={styles.percentageValue}>
                  {completedWebinars}/{totalWebinars}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </AscGenericCard>
  );
}
